import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertTriangle, Ban, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import moment from 'moment-timezone';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import { formatDateTimeRange } from '../../utils/dateTimeFormatter';

const toLocalInput = (date, timeZone) => moment(date).tz(timeZone).format('YYYY-MM-DDTHH:mm');
const toUtcInstant = (value, timeZone) => moment.tz(
  value,
  'YYYY-MM-DDTHH:mm',
  true,
  timeZone,
).toISOString();

const REASONS = {
  route: ['route_closure', 'weather', 'emergency', 'other'],
  vehicle: ['maintenance', 'breakdown', 'safety', 'other'],
};
const DISPLAY_STATUSES = new Set(['active', 'scheduled', 'expired', 'cancelled']);

const OperationalUnavailabilityPanel = ({
  entityId,
  targetType,
  targetId,
  driverMode = false,
  aggregateMode = false,
  routes = [],
  vehicles = [],
  routeStops = [],
  routeCatalogComplete = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { text, border, background, cn } = useTheme();
  const { entitySettings, userPreferences } = usePreferences();
  const configuredTimeZone = entitySettings?.time_zone;
  const timeZone = configuredTimeZone && moment.tz.zone(configuredTimeZone)
    ? configuredTimeZone
    : null;
  const preferredDateFormat =
    userPreferences?.date_format || userPreferences?.dateFormat || null;
  const preferredTimeFormat =
    userPreferences?.time_format || userPreferences?.timeFormat || null;
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [cancelBlock, setCancelBlock] = useState(null);
  const [conflictPreview, setConflictPreview] = useState(null);
  const [listMode, setListMode] = useState('current');
  const [displayedMode, setDisplayedMode] = useState('current');
  const notesWarningId = useId();
  const [filters, setFilters] = useState({
    resourceType: '',
    resourceId: '',
    startDate: '',
    endDate: '',
    status: '',
    search: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const requestGenerationRef = useRef(0);
  const translationRef = useRef(t);
  const [form, setForm] = useState(() => ({
    startsAt: '',
    endsAt: '',
    reasonCode: REASONS[targetType][0],
    notes: '',
    routeScope: 'whole_route',
    stopIds: [],
  }));

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(filters.search.trim());
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  const loadBlocks = useCallback(async () => {
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;
    setBlocks([]);
    if (!entityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {
        mode: aggregateMode ? listMode : 'current',
        page: pagination.page,
        limit: pagination.limit,
      };
      if (!aggregateMode && targetId) {
        params.resourceType = targetType;
        params[`${targetType}Id`] = targetId;
      }
      if (aggregateMode && listMode === 'history') {
        if (filters.resourceType) params.resourceType = filters.resourceType;
        if (filters.resourceType && filters.resourceId) {
          params[`${filters.resourceType}Id`] = filters.resourceId;
        }
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        if (filters.status) params.status = filters.status;
        if (debouncedSearch) params.search = debouncedSearch;
      }
      const response = await axios.get(
        `/api/operational-unavailability/${entityId}/search`,
        { params },
      );
      const data = response.data?.data || {};
      if (requestGenerationRef.current === requestGeneration) {
        setBlocks(data.blocks || []);
        setDisplayedMode(data.mode === 'history' ? 'history' : 'current');
        setPagination((current) => ({
          ...current,
          ...(data.pagination || {}),
        }));
      }
    } catch {
      if (requestGenerationRef.current === requestGeneration) {
        toast.error(translationRef.current('operationalUnavailability.loadFailed'));
      }
    } finally {
      if (requestGenerationRef.current === requestGeneration) {
        setLoading(false);
      }
    }
  }, [
    aggregateMode,
    debouncedSearch,
    entityId,
    filters.endDate,
    filters.resourceId,
    filters.resourceType,
    filters.startDate,
    filters.status,
    listMode,
    pagination.limit,
    pagination.page,
    targetId,
    targetType,
  ]);

  useEffect(() => {
    loadBlocks();
    return () => {
      // A late response from the old entity/target must never publish into the
      // current authorization context.
      requestGenerationRef.current += 1;
    };
  }, [loadBlocks]);

  useEffect(() => {
    // Target context changes invalidate open mutations as well as list state;
    // otherwise a dialog opened for one entity could submit against another.
    setShowCreate(false);
    setCancelBlock(null);
    setConflictPreview(null);
  }, [aggregateMode, driverMode, entityId, targetId, targetType]);

  useEffect(() => {
    // The form's lazy initializer runs only once. Re-derive the reason when an
    // admin switches Route/Shuttle scope so the untouched select cannot submit
    // a reason that is invalid for the new target type.
    setForm((current) => ({
      ...current,
      reasonCode: REASONS[targetType][0],
    }));
  }, [targetType]);

  useEffect(() => {
    if (!timeZone) return;
    setForm((current) => ({
      ...current,
      startsAt: current.startsAt || toLocalInput(new Date(), timeZone),
      endsAt: current.endsAt || toLocalInput(
        new Date(Date.now() + 2 * 60 * 60 * 1000),
        timeZone,
      ),
    }));
  }, [timeZone]);

  useEffect(() => {
    // A conflict acknowledgement is valid only for the exact previewed
    // window, reason, notes, and stop scope.
    setConflictPreview(null);
  }, [
    form.endsAt,
    form.notes,
    form.reasonCode,
    form.routeScope,
    form.startsAt,
    form.stopIds,
  ]);

  const displayBlocks = useMemo(() => blocks, [blocks]);

  const submit = async () => {
    if (!timeZone || !targetId) {
      toast.error(t('operationalUnavailability.timezoneUnavailable'));
      return;
    }
    try {
      setLoading(true);
      const targetPath = targetType === 'route' ? 'routes' : 'vehicles';
      const payload = {
        starts_at: driverMode ? undefined : toUtcInstant(form.startsAt, timeZone),
        ends_at: toUtcInstant(form.endsAt, timeZone),
        reason_code: form.reasonCode,
        notes: form.notes || null,
        ...(targetType === 'route' ? {
          route_scope: form.routeScope,
          stop_ids: form.routeScope === 'selected_stops' ? form.stopIds : [],
        } : {}),
      };
      if (!driverMode && !conflictPreview) {
        const previewResponse = await axios.post(
          `/api/operational-unavailability/${entityId}/${targetPath}/${targetId}/conflicts/preview`,
          payload,
        );
        const preview = previewResponse.data?.data || {};
        if ((preview.conflicts || []).length > 0 || preview.additional_conflicts_exist) {
          setConflictPreview(preview);
          return;
        }
      }
      await axios.post(
        `/api/operational-unavailability/${entityId}/${targetPath}/${targetId}`,
        payload,
      );
      setShowCreate(false);
      setConflictPreview(null);
      toast.success(t('operationalUnavailability.created'));
      await loadBlocks();
    } catch (error) {
      toast.error(error.response?.data?.error || t('operationalUnavailability.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!cancelBlock) return;
    try {
      setLoading(true);
      await axios.post(
        `/api/operational-unavailability/${entityId}/${cancelBlock.id}/cancel`,
        {},
      );
      setCancelBlock(null);
      toast.success(t('operationalUnavailability.cancelled'));
      await loadBlocks();
    } catch (error) {
      toast.error(error.response?.data?.error || t('operationalUnavailability.cancelFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className={cn(
        compact ? 'mt-3 pt-3' : 'mt-4 p-4 rounded-lg border',
        compact ? `border-t ${border.default}` : border.default,
        !compact && background.secondary,
      )}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className={cn('font-semibold flex items-center gap-2', text.primary)}>
            <Ban className="h-4 w-4" />
            {t('operationalUnavailability.title')}
          </h3>
          {!compact && (
            <p className={cn('text-sm mt-1', text.muted)}>
              {t(`operationalUnavailability.${targetType}Help`)}
            </p>
          )}
        </div>
        <button
          type="button"
          className="px-3 py-2 rounded inline-flex items-center gap-2 bg-amber-600 text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setShowCreate(true)}
          disabled={!timeZone || !targetId}
        >
          <Plus className="h-4 w-4" />
          {driverMode
            ? t('operationalUnavailability.reportVehicle')
            : t('operationalUnavailability.addBlock')}
        </button>
      </div>

      {!timeZone && (
        <p className="text-sm mt-3 text-red-600">
          {t('operationalUnavailability.timezoneUnavailable')}
        </p>
      )}

      {aggregateMode && (
        <div className="mt-4 space-y-3">
          <div className="flex gap-2" role="tablist">
            {['current', 'history'].map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={listMode === mode}
                className={cn(
                  'rounded border px-3 py-2 text-sm',
                  border.default,
                  listMode === mode ? 'bg-amber-600 text-white' : background.primary,
                )}
                onClick={() => {
                  setListMode(mode);
                  setPagination((current) => ({ ...current, page: 1 }));
                  if (mode === 'current') {
                    setFilters({
                      resourceType: '',
                      resourceId: '',
                      startDate: '',
                      endDate: '',
                      status: '',
                      search: '',
                    });
                  }
                }}
              >
                {t(`operationalUnavailability.listModes.${mode}`)}
              </button>
            ))}
          </div>
          {listMode === 'history' && (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <select
                aria-label={t('operationalUnavailability.resourceFilter')}
                value={filters.resourceType}
                onChange={(event) => {
                  setFilters((current) => ({
                    ...current,
                    resourceType: event.target.value,
                    resourceId: '',
                  }));
                  setPagination((current) => ({ ...current, page: 1 }));
                }}
                className={cn('rounded border px-3 py-2', background.primary, border.default)}
              >
                <option value="">{t('operationalUnavailability.allResources')}</option>
                <option value="route">{t('operations.route')}</option>
                <option value="vehicle">{t('operations.shuttle')}</option>
              </select>
              <select
                aria-label={t('operationalUnavailability.exactResource')}
                value={filters.resourceId}
                disabled={!filters.resourceType}
                onChange={(event) => {
                  setFilters((current) => ({ ...current, resourceId: event.target.value }));
                  setPagination((current) => ({ ...current, page: 1 }));
                }}
                className={cn(
                  'rounded border px-3 py-2 disabled:cursor-not-allowed disabled:opacity-60',
                  background.primary,
                  border.default,
                )}
              >
                <option value="">{t('operationalUnavailability.allSelectedResources')}</option>
                {(filters.resourceType === 'route'
                  ? routes
                  : (filters.resourceType === 'vehicle' ? vehicles : [])
                ).map((resource) => (
                  <option key={resource.id} value={resource.id}>
                    {filters.resourceType === 'route' ? resource.name : resource.vehicle_number}
                  </option>
                ))}
              </select>
              <select
                aria-label={t('operationalUnavailability.statusFilter')}
                value={filters.status}
                onChange={(event) => {
                  setFilters((current) => ({ ...current, status: event.target.value }));
                  setPagination((current) => ({ ...current, page: 1 }));
                }}
                className={cn('rounded border px-3 py-2', background.primary, border.default)}
              >
                <option value="">{t('operationalUnavailability.allStatuses')}</option>
                {['active', 'scheduled', 'expired', 'cancelled'].map((status) => (
                  <option key={status} value={status}>
                    {t(`operationalUnavailability.statuses.${status}`)}
                  </option>
                ))}
              </select>
              {['startDate', 'endDate'].map((field) => (
                <input
                  key={field}
                  type="date"
                  aria-label={t(`operationalUnavailability.${field}`)}
                  value={filters[field]}
                  onChange={(event) => {
                    setFilters((current) => ({ ...current, [field]: event.target.value }));
                    setPagination((current) => ({ ...current, page: 1 }));
                  }}
                  className={cn('rounded border px-3 py-2', background.primary, border.default)}
                />
              ))}
              <input
                type="search"
                aria-label={t('operationalUnavailability.search')}
                placeholder={t('operationalUnavailability.searchPlaceholder')}
                value={filters.search}
                onChange={(event) => {
                  setFilters((current) => ({ ...current, search: event.target.value }));
                  setPagination((current) => ({ ...current, page: 1 }));
                }}
                className={cn('rounded border px-3 py-2', background.primary, border.default)}
              />
            </div>
          )}
        </div>
      )}

      {loading && displayBlocks.length === 0 ? (
        <p className={cn('text-sm mt-3', text.muted)}>{t('loading')}...</p>
      ) : displayBlocks.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {displayBlocks.map((block) => {
            const status = DISPLAY_STATUSES.has(block.operational_status)
              ? block.operational_status
              : 'scheduled';
            const resourceName = block.route_id
              ? block.route_name
              : block.vehicle_number;
            const isSoleKnownRoute = Boolean(
              block.route_id
              && routeCatalogComplete
              && routes.length === 1
              && Number(routes[0]?.id) === Number(block.route_id),
            );
            const shouldShowResource = !block.route_id || !isSoleKnownRoute;
            const shouldShowStatus = displayedMode === 'history' || status !== 'scheduled';
            const selectedStops = block.route_scope === 'selected_stops'
              ? (Array.isArray(block.selected_stops) ? block.selected_stops : [])
                .map((stop, index) => ({
                  id: stop?.id ?? index,
                  name: String(stop?.name || '').trim(),
                }))
                .filter((stop) => stop.name)
              : [];
            const stopsHeadingId = `${notesWarningId}-block-${block.id}-stops`;
            const creatorUsername = String(block.created_by_username || '').trim()
              || t('operationalUnavailability.unknownCreator');
            const note = String(block.notes || '').trim();
            const summarySegments = [
              shouldShowStatus
                ? {
                  key: 'status',
                  content: t(`operationalUnavailability.statuses.${status}`),
                  strong: true,
                }
                : null,
              shouldShowResource
                ? {
                  key: 'resource',
                  content: resourceName || t('operationalUnavailability.unknownResource'),
                }
                : null,
              {
                key: 'reason',
                content: t(`operationalUnavailability.reasons.${block.reason_code}`),
              },
              {
                key: 'range',
                content: formatDateTimeRange(
                  block.starts_at,
                  block.ends_at,
                  preferredDateFormat,
                  preferredTimeFormat,
                  timeZone || 'UTC',
                ),
              },
              {
                key: 'creator',
                content: t('operationalUnavailability.createdByUsername', {
                  username: creatorUsername,
                }),
              },
            ].filter((segment) => segment?.content);
            return (
            <li
              key={block.id}
              className={cn(
                'text-sm flex flex-col gap-2 rounded p-2 sm:flex-row sm:items-start sm:justify-between',
                background.primary,
              )}
            >
              <div className="min-w-0 flex-1">
                <span className={cn('break-words [overflow-wrap:anywhere]', text.secondary)}>
                  {summarySegments.map((segment, index) => (
                    <Fragment key={segment.key}>
                      {index > 0 && ' · '}
                      {segment.strong
                        ? <strong>{segment.content}</strong>
                        : segment.content}
                    </Fragment>
                  ))}
                </span>
                {(selectedStops.length > 0 || (
                  block.route_scope === 'selected_stops'
                  && block.selected_stop_scope_inactive
                )) && (
                  <div className={cn(
                    'mt-1 break-words [overflow-wrap:anywhere]',
                    text.secondary,
                  )}>
                    {selectedStops.length > 0 && (
                      <>
                        <strong id={stopsHeadingId}>
                          {t('operationalUnavailability.stopsLabel')}
                        </strong>
                        <ul
                          aria-labelledby={stopsHeadingId}
                          className="mt-0.5 list-disc space-y-0.5 pl-5"
                        >
                          {selectedStops.map((stop) => (
                            <li key={`${block.id}-stop-${stop.id}`}>
                              {stop.name}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {block.selected_stop_scope_inactive && (
                      <p className={cn('mt-0.5 text-xs', text.muted)}>
                        {t('operationalUnavailability.selectedStopsInactive')}
                      </p>
                    )}
                  </div>
                )}
                {note && (
                  <p className={cn(
                    'mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]',
                    text.secondary,
                  )}>
                    <strong>{t('operationalUnavailability.noteLabel')}</strong>
                    {' '}
                    {note}
                  </p>
                )}
              </div>
              {block.can_cancel && (
                <button
                  type="button"
                  className="self-end shrink-0 text-red-600 hover:underline sm:self-auto"
                  onClick={() => setCancelBlock(block)}
                >
                  {t('operationalUnavailability.cancelBlock')}
                </button>
              )}
            </li>
            );
          })}
        </ul>
      ) : (
        <p className={cn('text-sm mt-3', text.muted)}>
          {t('operationalUnavailability.none')}
        </p>
      )}

      {pagination.totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm">
          <button
            type="button"
            disabled={pagination.page <= 1 || loading}
            onClick={() => setPagination((current) => ({
              ...current,
              page: Math.max(1, current.page - 1),
            }))}
          >
            {t('operationalUnavailability.previousPage')}
          </button>
          <span>
            {t('operationalUnavailability.pageOf', {
              page: pagination.page,
              total: pagination.totalPages,
            })}
          </span>
          <button
            type="button"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => setPagination((current) => ({
              ...current,
              page: current.page + 1,
            }))}
          >
            {t('operationalUnavailability.nextPage')}
          </button>
        </div>
      )}

      <ConfirmDialogShell
        isOpen={showCreate}
        onClose={() => !loading && setShowCreate(false)}
        onConfirm={submit}
        title={driverMode
          ? t('operationalUnavailability.reportVehicle')
          : t('operationalUnavailability.addBlock')}
        confirmLabel={t('operationalUnavailability.confirmBlock')}
        confirmVariant="warning"
        confirmIcon={AlertTriangle}
        isLoading={loading}
        isDisabled={
          !form.endsAt
          || (!driverMode && !form.startsAt)
          || (targetType === 'route'
            && form.routeScope === 'selected_stops'
            && form.stopIds.length === 0)
        }
        showIcon
        iconVariant="warning"
      >
        <p className={cn('text-sm', text.secondary)}>
          {driverMode
            ? t('operationalUnavailability.driverWarning')
            : t(`operationalUnavailability.${targetType}Help`)}
        </p>
        {conflictPreview && (
          <div className="rounded border border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
            {t('operationalUnavailability.conflictWarning', {
              count: conflictPreview.conflicts?.length || 0,
            })}
            {conflictPreview.additional_conflicts_exist
              ? ` ${t('operationalUnavailability.additionalHiddenConflicts')}`
              : ''}
          </div>
        )}
        {!driverMode && (
          <label className={cn('block text-sm', text.secondary)}>
            {t('operationalUnavailability.startsAt')}
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) => setForm((current) => ({
                ...current,
                startsAt: event.target.value,
              }))}
              className={cn('mt-1 w-full rounded border px-3 py-2', background.primary, border.default)}
            />
          </label>
        )}
        <label className={cn('block text-sm', text.secondary)}>
          {t('operationalUnavailability.endsAt')}
          <input
            type="datetime-local"
            value={form.endsAt}
            onChange={(event) => setForm((current) => ({
              ...current,
              endsAt: event.target.value,
            }))}
            className={cn('mt-1 w-full rounded border px-3 py-2', background.primary, border.default)}
          />
        </label>
        {targetType === 'route' && !driverMode && (
          <>
            <label className={cn('block text-sm', text.secondary)}>
              {t('operationalUnavailability.routeScope')}
              <select
                value={form.routeScope}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  routeScope: event.target.value,
                  stopIds: [],
                }))}
                className={cn('mt-1 w-full rounded border px-3 py-2', background.primary, border.default)}
              >
                <option value="whole_route">
                  {t('operationalUnavailability.wholeRoute')}
                </option>
                <option value="selected_stops">
                  {t('operationalUnavailability.selectedStops')}
                </option>
              </select>
            </label>
            {form.routeScope === 'selected_stops' && (
              <fieldset className={cn('rounded border p-3 text-sm', border.default)}>
                <legend className={text.secondary}>
                  {t('operationalUnavailability.selectStops')}
                </legend>
                <div className="mt-2 max-h-48 space-y-2 overflow-y-auto">
                  {routeStops.map((stop) => (
                    <label key={stop.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.stopIds.includes(Number(stop.id))}
                        onChange={(event) => setForm((current) => ({
                          ...current,
                          stopIds: event.target.checked
                            ? [...current.stopIds, Number(stop.id)]
                            : current.stopIds.filter((id) => id !== Number(stop.id)),
                        }))}
                      />
                      <span>{stop.name || stop.stop_name}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </>
        )}
        <label className={cn('block text-sm', text.secondary)}>
          {t('operationalUnavailability.reason')}
          <select
            value={form.reasonCode}
            onChange={(event) => setForm((current) => ({
              ...current,
              reasonCode: event.target.value,
            }))}
            className={cn('mt-1 w-full rounded border px-3 py-2', background.primary, border.default)}
          >
            {REASONS[targetType].map((reason) => (
              <option key={reason} value={reason}>
                {t(`operationalUnavailability.reasons.${reason}`)}
              </option>
            ))}
          </select>
        </label>
        <label className={cn('block text-sm', text.secondary)}>
          {t('operationalUnavailability.notes')}
          <textarea
            aria-describedby={notesWarningId}
            maxLength={500}
            value={form.notes}
            onChange={(event) => setForm((current) => ({
              ...current,
              notes: event.target.value,
            }))}
            className={cn('mt-1 w-full rounded border px-3 py-2', background.primary, border.default)}
          />
          <span id={notesWarningId} className={cn('mt-1 block text-xs', text.muted)}>
            {t('operationalUnavailability.notesAudienceWarning')}
          </span>
        </label>
      </ConfirmDialogShell>

      <ConfirmDialogShell
        isOpen={Boolean(cancelBlock)}
        onClose={() => !loading && setCancelBlock(null)}
        onConfirm={cancel}
        title={t('operationalUnavailability.cancelBlock')}
        confirmLabel={t('operationalUnavailability.cancelBlock')}
        confirmVariant="danger"
        confirmIcon={Ban}
        isLoading={loading}
        showIcon
        iconVariant="danger"
      >
        <p className={text.secondary}>
          {t('operationalUnavailability.cancelWarning')}
        </p>
      </ConfirmDialogShell>
    </section>
  );
};

export default OperationalUnavailabilityPanel;
